
import { CheckIcon, CopyIcon, Download } from 'lucide-react';
import { FC, memo } from 'react';
import { atomOneLight } from 'react-syntax-highlighter/dist/cjs/styles/hljs';
import { Button } from '../button';
import { SyntaxHighlighter } from '../syntax-highlighter';
import { useCopyToClipboard } from '@/hooks/use-copy-to-clipboard';

interface MessageCodeBlockProps {
  language: string;
  value: string;
}

interface languageMap {
  [key: string]: string | undefined;
}

export const programmingLanguages: languageMap = {
  javascript: '.js',
  python: '.py',
  java: '.java',
  c: '.c',
  cpp: '.cpp',
  'c++': '.cpp',
  'c#': '.cs',
  ruby: '.rb',
  php: '.php',
  swift: '.swift',
  'objective-c': '.m',
  kotlin: '.kt',
  typescript: '.ts',
  go: '.go',
  perl: '.pl',
  rust: '.rs',
  scala: '.scala',
  haskell: '.hs',
  lua: '.lua',
  shell: '.sh',
  sql: '.sql',
  html: '.html',
  css: '.css',
};

export const generateRandomString = (length: number, lowercase = false) => {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXY3456789'; // excluding similar looking characters like Z, 2, I, 1, O, 0
  let result = '';
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return lowercase ? result.toLowerCase() : result;
};

export const MessageCodeBlock: FC<MessageCodeBlockProps> = memo(
  ({ language, value }) => {
    const { isCopied, copyToClipboard } = useCopyToClipboard({ timeout: 2000 });

    const downloadAsFile = () => {
      if (typeof window === 'undefined') {
        return;
      }
      const fileExtension = programmingLanguages[language] || '.file';
      const suggestedFileName = `file-${generateRandomString(
        3,
        true
      )}${fileExtension}`;
      const fileName = window.prompt('Enter file name', suggestedFileName);

      if (!fileName) {
        return;
      }

      const blob = new Blob([value], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.download = fileName;
      link.href = url;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    };

    const onCopy = () => {
      if (isCopied) return;
      copyToClipboard(value);
    };

    return (
      <div className="relative w-full overflow-hidden rounded-md bg-[#fafafa] font-sans">
        <div className="flex w-full items-center justify-between px-4 py-2 text-white">
          <span className="text-xs lowercase text-gray-500">{language}</span>
          <div className="flex items-center space-x-2">
            <Button
              variant="ghost"
              size="icon"
              className="h-4 w-4 text-gray-500"
              onClick={downloadAsFile}
            >
              <Download size={12} />
            </Button>

            <Button
              variant="ghost"
              size="icon"
              className="h-4 w-4 text-gray-500"
              onClick={onCopy}
            >
              {isCopied ? <CheckIcon size={12} /> : <CopyIcon size={12} />}
            </Button>
          </div>
        </div>
        <SyntaxHighlighter
          language={language}
          style={atomOneLight}
        >
          {value}
        </SyntaxHighlighter>
      </div>
    );
  }
);

MessageCodeBlock.displayName = 'MessageCodeBlock';
